import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, serverTimestamp, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../firebase/config';
import foodDatabase from '../data/food-database.json';

/**
 * Nutrition Service
 * Handles food database operations similar to exerciseLibrary.js pattern
 */
class NutritionService {
  constructor() {
    this.defaultFoodsCache = null;
    this.customFoodsCache = null;
    this.recipesCache = null;
  }

  // ==================== FOOD OPERATIONS ====================

  /**
   * Get all default foods from JSON database
   * @returns {Array} Array of default food items
   */
  getDefaultFoods() {
    if (this.defaultFoodsCache) {
      return this.defaultFoodsCache;
    }

    const foods = [];
    const database = foodDatabase.foodDatabase;

    // Process each category
    Object.keys(database).forEach(category => {
      const categoryFoods = database[category];
      if (Array.isArray(categoryFoods)) {
        foods.push(...categoryFoods.map(food => ({
          ...food,
          id: `default_${food.name.toLowerCase().replace(/\s+/g, '_')}`,
          isDefault: true,
          searchTerms: food.name.toLowerCase().split(' ').filter(t => t.length > 2)
        })));
      }
    });

    this.defaultFoodsCache = foods;
    return foods;
  }

  /**
   * Get all foods from Firestore (verified foods added by coaches)
   * @returns {Promise<Array>} Array of food items
   */
  async getAllFirestoreFoods() {
    try {
      const foodsRef = collection(db, 'foods');
      const q = query(foodsRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      const foods = [];
      querySnapshot.forEach((doc) => {
        foods.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return foods;
    } catch (error) {
      console.error('[NutritionService] Error fetching Firestore foods:', error);
      return [];
    }
  }

  /**
   * Get custom foods for a coach
   * @param {string} coachId - Coach user ID
   * @returns {Promise<Array>} Array of custom food items
   */
  async getCustomFoods(coachId) {
    try {
      const foodsRef = collection(db, 'foods');
      const q = query(
        foodsRef,
        where('addedBy', '==', coachId),
        where('isVerified', '==', false),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const customFoods = [];

      querySnapshot.forEach((doc) => {
        customFoods.push({
          id: doc.id,
          ...doc.data(),
          isCustom: true
        });
      });

      this.customFoodsCache = customFoods;
      return customFoods;
    } catch (error) {
      console.error('[NutritionService] Error fetching custom foods:', error);
      return [];
    }
  }

  /**
   * Get all foods (default + Firestore verified + custom for coach)
   * @param {string} coachId - Coach user ID
   * @returns {Promise<Array>} Combined array of foods
   */
  async getAllFoodsForCoach(coachId) {
    const defaultFoods = this.getDefaultFoods();
    const firestoreFoods = await this.getAllFirestoreFoods();
    const customFoods = await this.getCustomFoods(coachId);

    // Combine: defaults + verified Firestore foods + coach's custom foods
    const verifiedFirestoreFoods = firestoreFoods.filter(food => food.isVerified);

    return [...defaultFoods, ...verifiedFirestoreFoods, ...customFoods];
  }

  /**
   * Search foods by term and filters
   * @param {Array} foods - Array of foods to search
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Filter options (category, minProtein, maxCalories, etc.)
   * @returns {Array} Filtered foods
   */
  searchFoods(foods, searchTerm, filters = {}) {
    let filtered = foods;

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(food =>
        food.name.toLowerCase().includes(term) ||
        food.brand?.toLowerCase().includes(term) ||
        food.category?.toLowerCase().includes(term) ||
        food.searchTerms?.some(t => t.includes(term))
      );
    }

    // Category filter
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(food => food.category === filters.category);
    }

    // Protein filter (minimum grams per 100g serving)
    if (filters.minProtein) {
      filtered = filtered.filter(food => food.protein >= filters.minProtein);
    }

    // Calorie filter (maximum calories per 100g serving)
    if (filters.maxCalories) {
      filtered = filtered.filter(food => food.calories <= filters.maxCalories);
    }

    // Carb filter (maximum carbs per 100g serving)
    if (filters.maxCarbs) {
      filtered = filtered.filter(food => food.carbs <= filters.maxCarbs);
    }

    // Fat filter (maximum fat per 100g serving)
    if (filters.maxFat) {
      filtered = filtered.filter(food => food.fat <= filters.maxFat);
    }

    // Verified only filter
    if (filters.verifiedOnly) {
      filtered = filtered.filter(food => food.isVerified);
    }

    return filtered;
  }

  /**
   * Create custom food
   * @param {string} coachId - Coach user ID
   * @param {Object} foodData - Food data
   * @returns {Promise<Object>} Created food with ID
   */
  async createCustomFood(coachId, foodData) {
    try {
      // Generate search terms
      const searchTerms = (foodData.name || '')
        .toLowerCase()
        .split(' ')
        .filter(term => term.length > 2);

      if (foodData.brand) {
        const brandTerms = foodData.brand
          .toLowerCase()
          .split(' ')
          .filter(term => term.length > 2);
        searchTerms.push(...brandTerms);
      }

      const newFood = {
        ...foodData,
        addedBy: coachId,
        isVerified: false,
        searchTerms: [...new Set(searchTerms)],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'foods'), newFood);

      // Update cache
      if (this.customFoodsCache) {
        this.customFoodsCache.push({
          id: docRef.id,
          ...newFood,
          createdAt: new Date(),
          updatedAt: new Date(),
          isCustom: true
        });
      }

      return { id: docRef.id, ...newFood };
    } catch (error) {
      console.error('[NutritionService] Error creating custom food:', error);
      throw error;
    }
  }

  /**
   * Update custom food
   * @param {string} foodId - Food document ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<boolean>} Success status
   */
  async updateCustomFood(foodId, updates) {
    try {
      const foodRef = doc(db, 'foods', foodId);

      // Regenerate search terms if name or brand changed
      if (updates.name || updates.brand) {
        const searchTerms = [];

        if (updates.name) {
          searchTerms.push(...updates.name.toLowerCase().split(' ').filter(t => t.length > 2));
        }

        if (updates.brand) {
          searchTerms.push(...updates.brand.toLowerCase().split(' ').filter(t => t.length > 2));
        }

        updates.searchTerms = [...new Set(searchTerms)];
      }

      await updateDoc(foodRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      // Update cache
      if (this.customFoodsCache) {
        const index = this.customFoodsCache.findIndex(f => f.id === foodId);
        if (index !== -1) {
          this.customFoodsCache[index] = {
            ...this.customFoodsCache[index],
            ...updates,
            updatedAt: new Date()
          };
        }
      }

      return true;
    } catch (error) {
      console.error('[NutritionService] Error updating custom food:', error);
      throw error;
    }
  }

  /**
   * Delete custom food
   * @param {string} foodId - Food document ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteCustomFood(foodId) {
    try {
      await deleteDoc(doc(db, 'foods', foodId));

      // Update cache
      if (this.customFoodsCache) {
        this.customFoodsCache = this.customFoodsCache.filter(f => f.id !== foodId);
      }

      return true;
    } catch (error) {
      console.error('[NutritionService] Error deleting custom food:', error);
      throw error;
    }
  }

  // ==================== RECIPE OPERATIONS ====================

  /**
   * Get all recipes for a coach
   * @param {string} coachId - Coach user ID
   * @returns {Promise<Array>} Array of recipes
   */
  async getRecipes(coachId) {
    try {
      const recipesRef = collection(db, 'recipes');
      const q = query(
        recipesRef,
        where('coachId', '==', coachId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const recipes = [];

      querySnapshot.forEach((doc) => {
        recipes.push({
          id: doc.id,
          ...doc.data()
        });
      });

      this.recipesCache = recipes;
      return recipes;
    } catch (error) {
      console.error('[NutritionService] Error fetching recipes:', error);
      return [];
    }
  }

  /**
   * Create recipe
   * @param {string} coachId - Coach user ID
   * @param {Object} recipeData - Recipe data with ingredients
   * @returns {Promise<Object>} Created recipe with ID
   */
  async createRecipe(coachId, recipeData) {
    try {
      const newRecipe = {
        ...recipeData,
        coachId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'recipes'), newRecipe);

      // Update cache
      if (this.recipesCache) {
        this.recipesCache.unshift({
          id: docRef.id,
          ...newRecipe,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      return { id: docRef.id, ...newRecipe };
    } catch (error) {
      console.error('[NutritionService] Error creating recipe:', error);
      throw error;
    }
  }

  /**
   * Calculate recipe nutrition totals from ingredients
   * @param {Array} ingredients - Array of ingredient objects
   * @returns {Object} Total nutrition values
   */
  calculateRecipeNutrition(ingredients) {
    return ingredients.reduce((totals, ingredient) => {
      const multiplier = ingredient.quantity / 100; // Assuming base values are per 100g

      return {
        calories: totals.calories + (ingredient.food.calories * multiplier),
        protein: totals.protein + (ingredient.food.protein * multiplier),
        carbs: totals.carbs + (ingredient.food.carbs * multiplier),
        fat: totals.fat + (ingredient.food.fat * multiplier),
        fiber: totals.fiber + ((ingredient.food.fiber || 0) * multiplier),
        sugar: totals.sugar + ((ingredient.food.sugar || 0) * multiplier)
      };
    }, {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      sugar: 0
    });
  }

  // ==================== NUTRITION PLAN OPERATIONS ====================

  /**
   * Create nutrition plan
   * @param {Object} planData - Nutrition plan data
   * @returns {Promise<Object>} Created plan with ID
   */
  async createNutritionPlan(planData) {
    try {
      const newPlan = {
        ...planData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'nutritionPlans'), newPlan);

      return { id: docRef.id, ...newPlan };
    } catch (error) {
      console.error('[NutritionService] Error creating nutrition plan:', error);
      throw error;
    }
  }

  /**
   * Get nutrition plans for a client
   * @param {string} clientId - Client user ID
   * @returns {Promise<Array>} Array of nutrition plans
   */
  async getClientNutritionPlans(clientId) {
    try {
      const plansRef = collection(db, 'nutritionPlans');
      const q = query(
        plansRef,
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const plans = [];

      querySnapshot.forEach((doc) => {
        plans.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return plans;
    } catch (error) {
      console.error('[NutritionService] Error fetching nutrition plans:', error);
      return [];
    }
  }

  /**
   * Update nutrition plan
   * @param {string} planId - Plan document ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<boolean>} Success status
   */
  async updateNutritionPlan(planId, updates) {
    try {
      const planRef = doc(db, 'nutritionPlans', planId);
      await updateDoc(planRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('[NutritionService] Error updating nutrition plan:', error);
      throw error;
    }
  }

  // ==================== CATEGORY & FILTER HELPERS ====================

  /**
   * Get food categories
   * @returns {Array} Array of category objects
   */
  getFoodCategories() {
    return [
      { id: 'all', name: 'All Foods' },
      { id: 'Protein', name: 'Protein' },
      { id: 'ComplexCarbs', name: 'Complex Carbs' },
      { id: 'FibrousCarbs', name: 'Fibrous Carbs / Vegetables' },
      { id: 'Fruits', name: 'Fruits' },
      { id: 'Dairy', name: 'Dairy' },
      { id: 'Fats', name: 'Healthy Fats' },
      { id: 'Supplements', name: 'Supplements' },
      { id: 'Condiments', name: 'Condiments & Seasonings' },
      { id: 'Beverages', name: 'Beverages' },
      { id: 'Takeaway', name: 'Takeaway / Restaurant' }
    ];
  }

  /**
   * Get macro filter presets
   * @returns {Array} Array of macro filter presets
   */
  getMacroFilterPresets() {
    return [
      { id: 'high_protein', name: 'High Protein (>20g)', minProtein: 20 },
      { id: 'low_carb', name: 'Low Carb (<10g)', maxCarbs: 10 },
      { id: 'low_fat', name: 'Low Fat (<5g)', maxFat: 5 },
      { id: 'low_calorie', name: 'Low Calorie (<100cal)', maxCalories: 100 }
    ];
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.foodsCache = null;
    this.customFoodsCache = null;
    this.recipesCache = null;
  }
}

export default new NutritionService();
